import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useBuildingConfigQuery,
  authenticatedApiClient,
  unauthenticatedApiClient,
  FEATURE_FLAG_ENDPOINT,
  BUILDING_KEYS,
  MAINTENANCE_MODE_ENDPOINT,
  setMaintenanceModeHandler,
  setAppliedStateVersion,
  userService,
  type OneAppUserResolveRequest,
  type OneAppUserResolveResponse,
} from '@cobu/api-client';
import { Storage } from '@cobu/platform-web';
import type { RootState, AppDispatch } from '../store';
import {
  setAuthFromToken,
  setLoading,
  updateNativePermission,
  setNativePermissionsProvided,
  setLanguage as setReduxLanguage,
  setPendingContactToken,
} from '../store/authSlice';
import { Strings } from '../constants/Strings';
import { setDateLocale } from '../utils/dateTime';
import {
  setBuildingConfig,
  clearSettings,
  saveChatPollingFeatureFlag,
  saveTooltipPollingFeatureFlag,
  setRestriction,
} from '../store/settingsSlice';
import { initSession, type ResolveUserParams } from '../lib/session/token';
import { BuildingConfig, UserInfo } from '@cobu/types';
import { analyticsService } from '@cobu/analytics';
import { useGetUniversalButtonDataQuery } from './universalButton/universalButtonHooks';
import { bootIntercomForExistingContact } from '../intercom';
import { logger } from '../utils/logger';
import { mapDeepLinkToRoute } from '../lib/deeplink';
import { handleResolveUserError, isSelfExplanatoryError } from '../lib/session/resolveErrorHandler';
import { deriveFeatureFlags, useFeatureFlags } from './useFeatureFlags';
import { useRestrictionLiveRefresh } from './useRestrictionLiveRefresh';

interface UserSessionInitResult {
  isLoading: boolean;
  error: string | null;
  isErrorMessageSelfExplanatory: boolean;
  isMaintenanceMode: boolean;
}

export const useUserSessionInit = (): UserSessionInitResult => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useSelector((state: RootState) => state.auth);
  const { buildingConfig } = useSelector((state: RootState) => state.settings);
  const hookFlags = useFeatureFlags();
  const [error, setError] = useState<string | null>(null);
  const [isErrorMessageSelfExplanatory, setIsErrorMessageSelfExplanatory] = useState(false);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [hasHydratedUser, setHasHydratedUser] = useState(false);
  const [hasBundledFlags, setHasBundledFlags] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const hasInitializedSession = useRef(false);

  const handleResolvedLink = useCallback(
    (link?: string, resolvedConfig?: BuildingConfig | null) => {
      if (!link) return;
      // The resolve response's config is dispatched in the same tick as this
      // call, so the flags from the hook are still the pre-resolve defaults
      // here. Derive from the config we were just handed when there is one —
      // otherwise Places (which defaults to off) would never open a deeplink.
      const { showGroups, residentsCanCreateEvents, showEvents, showPlaces } = resolvedConfig
        ? deriveFeatureFlags(resolvedConfig)
        : hookFlags;
      const route = mapDeepLinkToRoute(link, { showGroups, residentsCanCreateEvents, showEvents, showPlaces });
      if (!route) {
        if (logger && typeof logger.info === 'function') {
          logger.info('[SessionInit] Received deeplink link without route mapping:', link);
        }
        return;
      }
      navigate(route);
    },
    [navigate, hookFlags]
  );

  const buildingUuid = auth.user?.building?.uuid;
  const shouldFetchBuildingConfig = hasHydratedUser && !!buildingUuid;

  const {
    data: buildingConfigData,
    isPending: isBuildingConfigPending,
    isFetching: isBuildingConfigFetching,
    error: buildingConfigError,
  } = useBuildingConfigQuery(undefined, {
    enabled: shouldFetchBuildingConfig,
    placeholderData: buildingConfig,
  });

  useGetUniversalButtonDataQuery(buildingUuid || '');

  const hydrateUserFromToken = useCallback(
    async (sessionData: ResolveUserParams) => {
      try {
        const requestParams: OneAppUserResolveRequest = {
          authToken: sessionData.authToken,
          homebodyUserUuid: sessionData.homebodyUserUuid,
          emailAddress: sessionData.emailAddress,
          firstName: sessionData.firstName,
          lastName: sessionData.lastName,
          phoneNumber: sessionData.phoneNumber,
          buildingName: sessionData.buildingName,
          cid: sessionData.cid,
          clientId: sessionData.clientId,
          buildingId: sessionData.buildingId,
          propertyId: sessionData.propertyId,
          leaseStart: sessionData.leaseStart,
          leaseEnd: sessionData.leaseEnd,
          leaseStatus: sessionData.leaseStatus,
          deeplink: sessionData.deeplink,
          appVersion: import.meta.env.VITE_APP_VERSION as string,
          language: sessionData.language,
        };

        const resolved: OneAppUserResolveResponse =
          await userService.resolveOneAppUser(requestParams);

        const user: UserInfo = resolved.user;
        const token = resolved.token;
        const streamToken =
          resolved.streamToken ??
          (import.meta.env.VITE_STREAM_USER_TOKEN as string);

        try {
          const searchParams = new URLSearchParams(window.location.search);
          const deeplinkFromUrl = searchParams.get('deeplink');
          if (deeplinkFromUrl) {
            sessionData.deeplink = deeplinkFromUrl;
          }
        } catch {
          // window or URLSearchParams not available
        }

        await Storage.set('app_token', token);

        setHasHydratedUser(true);
        dispatch(setAuthFromToken({ token, user, streamToken }));

        // Until the terms are accepted the backend holds no email/phone for this
        // resident; the welcome screen hands this token back on consent.
        dispatch(setPendingContactToken(resolved.pendingContactToken ?? null));

        if (resolved.featureFlags) {
          dispatch(saveChatPollingFeatureFlag(resolved.featureFlags.chatPolling ?? true));
          dispatch(saveTooltipPollingFeatureFlag(resolved.featureFlags.tooltipPolling ?? true));
          setHasBundledFlags(true);
        }

        if (resolved.buildingConfig) {
          const version = (import.meta.env.VITE_APP_VERSION as string) || '';
          queryClient.setQueryData(BUILDING_KEYS.buildingConfig(version), resolved.buildingConfig);
          dispatch(setBuildingConfig(resolved.buildingConfig));
          analyticsService.setBuildingAnalytics(resolved.buildingConfig.analyticsEnabled ?? true);
          // Cache is seeded — handleBuildingConfigInit will call setIsInitializing(false)
          // in the next render cycle without triggering a separate fetch
        }

        dispatch(setRestriction(resolved.restriction ?? { restricted: false }));
        // Seed the live-refresh baseline; if absent, the first authenticated
        // response adopts its version instead.
        setAppliedStateVersion(resolved.restriction?.stateVersion);

        dispatch(setNativePermissionsProvided(Boolean(sessionData.nativePermissions)));
        if (sessionData.nativePermissions) {
          Object.entries(sessionData.nativePermissions).forEach(
            ([permission, granted]) => {
              dispatch(
                updateNativePermission({
                  permission: permission as 'camera' | 'library' | 'calendar',
                  granted: Boolean(granted),
                })
              );
            }
          );
        }

        // Deferred so it doesn't compete with Stream chat initialization.
        setTimeout(() => bootIntercomForExistingContact(user), 5000);

        analyticsService
          .identifyUser(user.uuid, {
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            building: user.building?.name,
            buildingId: user.building?.uuid,
            roomNumber: user.roomNumber,
            interests: user.interests?.map((i: any) => i?.name),
            receiveNotifications: user.receiveNotifications,
            acceptedTermsAndConditions: user.acceptedTermsAndConditions,
          })
          .catch(err => logger.error('[Analytics] Identify failed:', err));

        analyticsService
          .trackEvent('user_authorized', {
            userId: user.uuid,
            buildingId: user.building?.uuid,
            buildingName: user.building?.name,
            email: user.email,
          })
          .catch(err => logger.error('[Analytics] Track failed:', err));

        // Cast: the resolve response types buildingConfig as a loose record.
        handleResolvedLink(sessionData.deeplink, resolved.buildingConfig as BuildingConfig | undefined);
      } catch (err: unknown) {
        const { message, classification } = handleResolveUserError(err);
        logger.error('[UserSessionInit] Failed to resolve user via OneApp', err);
        setError(message);
        setIsErrorMessageSelfExplanatory(isSelfExplanatoryError(classification));
        setIsInitializing(false);
      }
    },
    [dispatch, handleResolvedLink, queryClient]
  );

  const initializeSession = useCallback(async () => {
    if (hasInitializedSession.current) return;
    hasInitializedSession.current = true;

    dispatch(setLoading(true));
    setIsInitializing(true);

    try {
      const session = await initSession();

      // Apply the language hint from the session (URL ?language= or postMessage)
      // before any authenticated network requests fire — that way error toasts
      // and any subsequent UI render in the user's language.
      if (session.language && session.language !== Strings.getLanguage()) {
        try {
          await Strings.setLanguage(session.language);
        } catch (err) {
          logger.error('[i18n] Failed to switch language from session:', err);
        }
      }
      if (session.language) {
        setDateLocale(session.language);
        dispatch(setReduxLanguage(session.language));
      }

      try {
        const maintenanceResponse = await unauthenticatedApiClient.get<{ maintenance_mode: boolean }>(
          MAINTENANCE_MODE_ENDPOINT
        );
        if (maintenanceResponse.maintenance_mode) {
          setIsMaintenanceMode(true);
          setIsInitializing(false);
          return;
        }
      } catch {
        // If the maintenance check fails, proceed normally
      }

      await hydrateUserFromToken(session);
    } catch (err: any) {
      setError(err?.message || 'Auth error');
      setIsInitializing(false);
      hasInitializedSession.current = false;
    }
  }, [dispatch, hydrateUserFromToken]);

  const loadFeatureFlags = useCallback(async () => {
    try {
      const featureFlags = await authenticatedApiClient.get<
        { chatPolling: boolean; tooltipPolling: boolean }[]
      >(FEATURE_FLAG_ENDPOINT);

      const chatPollingFlag = featureFlags?.[0]?.chatPolling ?? true;
      const tooltipPollingFlag = featureFlags?.[0]?.tooltipPolling ?? true;

      dispatch(saveChatPollingFeatureFlag(chatPollingFlag));
      dispatch(saveTooltipPollingFeatureFlag(tooltipPollingFlag));
    } catch (error) {
      logger.error('[Feature Flags] Error fetching feature flags:', error);
    }
  }, [dispatch]);

  const handleBuildingConfigInit = useCallback(() => {
    if (!hasHydratedUser) return;

    if (!shouldFetchBuildingConfig) {
      dispatch(clearSettings());
      setIsInitializing(false);
      return;
    }

    if (buildingConfigError) {
      setError(
        buildingConfigError.message || 'Failed to fetch building configuration'
      );
      setIsInitializing(false);
      return;
    }

    if (!isBuildingConfigPending && !isBuildingConfigFetching) {
      if (buildingConfigData !== undefined) {
        dispatch(setBuildingConfig(buildingConfigData));
      }
      setIsInitializing(false);
    }
  }, [
    buildingConfigData,
    buildingConfigError,
    dispatch,
    hasHydratedUser,
    isBuildingConfigFetching,
    isBuildingConfigPending,
    shouldFetchBuildingConfig,
  ]);

  useEffect(() => {
    setMaintenanceModeHandler(() => {
      setIsMaintenanceMode(true);
      setIsInitializing(false);
    });
  }, []);

  // Re-gates the UI on a mid-session block/unblock, without a reload.
  useRestrictionLiveRefresh(hasHydratedUser);

  useEffect(() => {
    if (!hasHydratedUser && !hasInitializedSession.current) {
      void initializeSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasHydratedUser) return;

    const run = async () => {
      const flagsPromise = hasBundledFlags ? Promise.resolve() : loadFeatureFlags();
      await Promise.all([flagsPromise, Promise.resolve(handleBuildingConfigInit())]);
    };

    void run();
  }, [
    auth.user?.building?.uuid,
    handleBuildingConfigInit,
    hasHydratedUser,
    hasBundledFlags,
    loadFeatureFlags,
  ]);

  const isLoading = useMemo(
    () => isInitializing || auth.isLoading,
    [auth.isLoading, isInitializing]
  );

  return {
    isLoading,
    error,
    isErrorMessageSelfExplanatory,
    isMaintenanceMode,
  };
};
